// ============================================================================
// HomeGameLedgersPage - archived settle-up sheets for host-banked home games
// ============================================================================
// The app never moves money for home games, so the ledger IS how players
// settle up. Live games show it in Real Time Results at the table; this
// page shows the archived sheet for every closed game the user played in
// (saved automatically before any table deletion, including cleanup
// sweeps, so an accidental shutdown can never lose it).
// ============================================================================
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Spinner, Button,
} from '@chakra-ui/react';
import { Share2 } from 'lucide-react';
import { gameApiService } from '../services/gameApiService';
import { auth } from '../firebase';

interface LedgerRow { uid: string; name: string; buyIn: number; cashedOut: number; finalStack: number; net: number }
interface Ledger { tableId: string; tableName: string; ownerId: string | null; rows: LedgerRow[]; closedAt: number | null; reason: string | null }

const CARD_BG = '#0f1722';
const UP = '#4ade80';
const DOWN = '#f87171';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
const fmtDate = (ms: number | null) => ms
  ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '';

// Plain-text version of one settle-up sheet - what gets shared/exported.
// Text travels everywhere (Messages, WhatsApp, Notes, email) with no
// attachment handling, which is exactly what a home game group needs.
const buildLedgerText = (l: Ledger): string => {
  const lines: string[] = [
    `${l.tableName} - Home Game Results`,
    fmtDate(l.closedAt),
    '',
  ];
  [...l.rows].sort((a, b) => b.net - a.net).forEach(r => {
    const sign = r.net > 0 ? '+' : '';
    lines.push(`${r.name}: chips received ${fmt(r.buyIn)}, net ${sign}${fmt(r.net)}`);
  });
  lines.push('');
  lines.push('Session chips are scorekeeping only and have no cash value.');
  lines.push('Recorded on PokerSweepStars');
  return lines.join('\n');
};

const HomeGameLedgersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  // tableId whose ledger was just copied to the clipboard (desktop fallback)
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const myUid = auth.currentUser?.uid || '';

  // Native share sheet where available (iOS/Android - WKWebView supports the
  // Web Share API), clipboard copy on desktop, last-ditch alert if both fail.
  const handleShare = async (l: Ledger) => {
    const text = buildLedgerText(l);
    if (navigator.share) {
      try {
        await navigator.share({ title: `${l.tableName} ledger`, text });
        return;
      } catch (e: any) {
        if (e?.name === 'AbortError') return; // user closed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(l.tableId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert(text);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await gameApiService.getMyHomeGameLedgers();
      if (cancelled) return;
      if (r.success) setLedgers(r.ledgers || []);
      else setError(r.error || 'Could not load your ledgers');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box minH="100vh" bg="#0a1420" pb={12}>
      <Container maxW="container.md" pt={6}>
        <HStack justify="space-between" mb={2}>
          <Heading size="lg" color="white">Home Game Ledgers</Heading>
          <Button size="sm" variant="outline" colorScheme="cyan" onClick={() => navigate('/profile')}>
            Back
          </Button>
        </HStack>
        <Text fontSize="sm" color="gray.500" mb={6}>
          Final scorekeeping sheets from your home games. Chips are
          session-only with no cash value, and every sheet's nets sum to zero.
        </Text>

        {loading ? (
          <Box textAlign="center" py={20}><Spinner color="cyan.400" size="lg" /></Box>
        ) : error ? (
          <Box bg={CARD_BG} border="1px solid" borderColor="red.500" borderRadius="xl" p={6} textAlign="center">
            <Text color="red.300">{error}</Text>
          </Box>
        ) : ledgers.length === 0 ? (
          <Box bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" p={8} textAlign="center">
            <Text color="gray.400" fontSize="sm">
              No closed home games yet. Host one from the tables page and the
              results sheet will be saved here automatically when it ends.
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={5}>
            {ledgers.map(l => (
              <Box key={l.tableId} bg={CARD_BG} border="1px solid rgba(139,92,246,0.35)" borderRadius="xl" overflow="hidden">
                <HStack justify="space-between" px={5} pt={4} pb={2}>
                  <Box>
                    <Text color="white" fontSize="md" fontWeight="800">{l.tableName}</Text>
                    <Text color="gray.500" fontSize="xs" mt={0.5}>
                      {fmtDate(l.closedAt)}{l.ownerId === myUid ? ' - you hosted' : ''}
                    </Text>
                  </Box>
                  <Button size="xs" variant="outline" colorScheme="cyan"
                    leftIcon={<Share2 size={13} />} onClick={() => handleShare(l)}>
                    {copiedId === l.tableId ? 'Copied' : 'Share'}
                  </Button>
                </HStack>
                <HStack px={5} py={2} borderBottom="1px solid rgba(148,163,184,0.15)">
                  <Text flex={1} color="gray.600" fontSize="xs" fontWeight="700">PLAYER</Text>
                  <Text w="90px" textAlign="right" color="gray.600" fontSize="xs" fontWeight="700">RECEIVED</Text>
                  <Text w="90px" textAlign="right" color="gray.600" fontSize="xs" fontWeight="700">NET</Text>
                </HStack>
                {[...l.rows].sort((a, b) => b.net - a.net).map(r => (
                  <HStack key={r.uid} px={5} py={2.5} borderBottom="1px solid rgba(148,163,184,0.06)">
                    <Text flex={1} color={r.uid === myUid ? 'cyan.200' : 'gray.200'} fontSize="sm" fontWeight="600" noOfLines={1}>
                      {r.name}{r.uid === myUid ? ' (you)' : ''}
                    </Text>
                    <Text w="90px" textAlign="right" color="gray.400" fontSize="sm">{fmt(r.buyIn)}</Text>
                    <Text w="90px" textAlign="right" fontSize="sm" fontWeight="800"
                      color={r.net > 0 ? UP : r.net < 0 ? DOWN : 'gray.400'}>
                      {r.net > 0 ? '+' : ''}{fmt(r.net)}
                    </Text>
                  </HStack>
                ))}
                <Text px={5} py={3} color="gray.600" fontSize="xs">
                  Session chips were scorekeeping only, with no cash value.
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default HomeGameLedgersPage;
